import cx from "classnames";
import omit from "lodash-es/omit";
import React, { createRef, useCallback } from "react";
import { v4 as uuid } from "uuid";

import FacetsFilter, { getFacetCount } from "@/mk2/apps/bazaar/components/FacetsFilter";
import Grid, { GridPlaceholder } from "@/mk2/apps/bazaar/components/Grid";
import { GridPageLogic } from "@/mk2/apps/bazaar/components/GridPageLogic";
import QualityCheckboxes from "@/mk2/apps/bazaar/components/QualityCheckboxes";
import SimpleSelect from "@/mk2/apps/bazaar/components/SimpleSelect";
import { SORTING_VALUES } from "@/mk2/apps/bazaar/components/SortLabel";
import TextSearch from "@/mk2/apps/bazaar/components/TextSearch";
import Watchdog from "@/mk2/apps/bazaar/components/Watchdog";
import { ITEMS_PER_PAGE } from "@/mk2/apps/bazaar/constants";
import { Props as PageProps } from "@/mk2/apps/bazaar/containers/BazaarGrid/BazaarGridPage";
import { hashFilter, watchdogKey } from "@/mk2/apps/bazaar/helpers/filter";
import useGrid, { GridProps } from "@/mk2/apps/bazaar/hooks/useGrid";
import { ProductEntity, ProductQuality } from "@/mk2/apps/bazaar/schemas";
import StrollerbaseBanner from "@/mk2/apps/home/components/StrollerbaseBanner";
import { StrollerEntity } from "@/mk2/apps/strollers/schemas";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { Pager } from "@/mk2/components/Pager";
import { Panel } from "@/mk2/components/Panel";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { getLogger } from "@/mk2/logger";
import { LoadingState, isLoading, isSuccess } from "@/mk2/reducers/loadingState";
import { interpolate } from "@/mk2/services/i18n";
import {
    BAZAAR_GRID_COPY_FILTER,
    BAZAAR_GRID_COPY_FILTER_PROMPT,
    BAZAAR_GRID_COPY_FILTER_SUCCESS,
    BZ2_COMMERCIAL_FIRST,
    BZ2_FILTER_SECTION_PRODUCTS_ALL,
    BZ2_FILTER_SECTION_PRODUCTS_FROM_LIKED_SELLERS,
    BZ2_ONLY_NOT_MARKED,
    BZ2_ONLY_REPORTED,
    BZ2_SEARCH_IN,
    BZ2_SEARCH_IN_ALL_AND_USERS,
    BZ2_counter_PRODUCTS,
} from "@/mk/autogenerated/translation";
import { formatThousands } from "@/mk/bazaar/common/formatters";
import {
    canEditWiki,
    canManageBazaar,
    canReportProducts,
    canUseCleanSort,
    isAuthenticated,
} from "@/mk/bazaar/common/userUtils";
import { getLabel, getTitleForCategoriesFilter } from "@/mk/bazaar/product/categories";
import { Filter } from "@/mk/bazaar/product/filter/filterUtils";

import styles from "./BazaarGrid.mscss";

const logger = getLogger("bazaar/containers/BazaarGrid/BazaarGrid");

interface OwnProps {
    onLoad();
}

type Props = OwnProps & Omit<PageProps, "onLoad"> & GridProps;

const sellersValues = {
    all: BZ2_FILTER_SECTION_PRODUCTS_ALL,
    liked: BZ2_FILTER_SECTION_PRODUCTS_FROM_LIKED_SELLERS,
};

class BazaarGrid extends React.Component<Props> {
    private productsRef = createRef<HTMLDivElement>();

    private cacheSelectedFacets = cacheLast();
    private cachePrice = cacheLast();
    private cacheSortingValues = cacheLast(true);

    // Used as a detection when we should trigger view event
    private lastFilterHash: string;
    private viewUuid: string;

    public componentDidMount() {
        this.trackView();
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.filter.page !== this.props.filter.page) {
            this.productsRef.current.scrollIntoView(true);
        }

        // Visible products has changed - track view
        if (prevProps.products !== this.props.products) {
            this.trackView();
        }
    }

    public render() {
        const {
            loadingState,
            history,
            isMobile,
            requestUserPermissions,
            requestUser,
            location,
            watchdog,
            openedFacet,
            filter,
            facets,
            hits,
            products,
            onSearchChange,
            onFacetClear,
            onFacetChange,
            onCategoryClear,
            onCategoryChange,
            onPriceClear,
            onPriceChange,
            onGridSortChange,
            onQualityChange,
            onSellersChange,
            generateCategoryTo,
            pagerTo,
            onFacetToggle,
            onLoad,
            onPlacesRadiusChange,
        } = this.props;

        const isModerator = canReportProducts(requestUserPermissions);
        const sortingValues: Partial<typeof SORTING_VALUES> = this.cacheSortingValues(
            () => {
                let vals: Partial<typeof SORTING_VALUES> = filter.q
                    ? SORTING_VALUES
                    : omit(SORTING_VALUES, "score_new");
                if (!watchdog) {
                    vals = omit(vals, "watchdog");
                }
                vals = isModerator
                    ? vals
                    : omit(
                          vals,
                          "complaints",
                          "complaints_asc",
                          "complaint_time",
                          "complaint_time_asc",
                          "score",
                      );

                return vals;
            },
            filter.q,
            watchdog,
            isModerator,
        );

        const maxPage = hits ? Math.ceil(hits / ITEMS_PER_PAGE) : 1;

        const selectedCat = filter.cats.length ? filter.cats[0] : null;
        const searchPlaceholder = selectedCat
            ? BZ2_SEARCH_IN + " " + getLabel(selectedCat)
            : BZ2_SEARCH_IN_ALL_AND_USERS;

        const quality = filter.settings.quality;

        let newCount = 0;
        let oldCount = 0;
        let handmadeCount = 0;
        let servicesCount = 0;
        let jobsCount = 0;

        // For empty results or blocked seller - facets is empty object
        if (facets?.quality) {
            const newFacet = facets.quality.find((q) => q[0] === ProductQuality.NEW);
            const oldFacet = facets.quality.find((q) => q[0] === ProductQuality.OLD);
            const handmadeFacet = facets.quality.find((q) => q[0] === ProductQuality.HANDMADE);
            const servicesFacet = facets.quality.find((q) => q[0] === ProductQuality.SERVICES);
            const jobsFacet = facets.quality.find((q) => q[0] === ProductQuality.JOBS);
            newCount = getFacetCount(newFacet);
            oldCount = getFacetCount(oldFacet);
            handmadeCount = getFacetCount(handmadeFacet);
            servicesCount = getFacetCount(servicesFacet);
            jobsCount = getFacetCount(jobsFacet);
        }

        const hasSomeFilter =
            filter.cats.length ||
            filter.locations.length ||
            filter.price_hi ||
            filter.price_lo ||
            filter.q ||
            Object.keys(filter.props).some((v) => filter.props[v].length > 0);

        const gridSort =
            filter.settings.gridSort === "watchdog" && !watchdog
                ? "creation"
                : filter.settings.gridSort;

        return (
            <div className={styles.BazaarGrid}>
                <div className={styles.BazaarGrid__headline}>
                    <h1 className={styles.BazaarGrid__headline__heading}>
                        {getTitleForCategoriesFilter(filter)}
                    </h1>
                </div>

                <div className={styles.BazaarGrid__filtering}>
                    <TextSearch
                        value={filter.q}
                        onChange={onSearchChange}
                        placeholder={searchPlaceholder}
                        hideButtonUntilFocused
                    />
                    {facets && (
                        <FacetsFilter
                            isMobile={isMobile}
                            facets={facets}
                            category={filter.cats[0]}
                            selectedFacets={this.cacheSelectedFacets(
                                {
                                    locations: filter.locations,
                                    places: filter.places,
                                    ...filter.props,
                                },
                                filter.locations,
                                filter.places,
                                filter.props,
                            )}
                            price={this.cachePrice(
                                {
                                    min: filter.price_lo,
                                    max: filter.price_hi,
                                },
                                filter.price_lo,
                                filter.price_hi,
                            )}
                            placesRadius={filter.settings.placesRadius}
                            onFacetClear={onFacetClear}
                            onFacetChange={onFacetChange}
                            onCategoryClear={onCategoryClear}
                            onCategoryChange={onCategoryChange}
                            onFacetToggle={onFacetToggle}
                            onPriceClear={onPriceClear}
                            onPriceChange={onPriceChange}
                            generateCategoryTo={generateCategoryTo}
                            openCategoriesIfEmpty={!isMobile || openedFacet === "cats"}
                            onPlacesRadiusChange={onPlacesRadiusChange}
                        />
                    )}
                </div>

                {isMobile ? (
                    <div>
                        <div className={styles.BazaarGrid__hitsCount}>
                            <div className={styles.BazaarGrid__hitsCount__line} />
                            <h4 className={styles.BazaarGrid__hitsCount__products}>
                                {isLoading(loadingState) ? (
                                    <LoadingPlaceholder width={78} />
                                ) : (
                                    interpolate(BZ2_counter_PRODUCTS, {
                                        counter: formatThousands(hits),
                                        count: hits,
                                    })
                                )}
                            </h4>
                            <div className={styles.BazaarGrid__hitsCount__line} />
                        </div>
                        {hits !== null && (
                            <QualityCheckboxes
                                qualities={quality}
                                handmadeCount={handmadeCount}
                                jobsCount={jobsCount}
                                newCount={newCount}
                                newCountHidden={selectedCat === "prams"}
                                oldCount={oldCount}
                                onChange={onQualityChange}
                                servicesCount={servicesCount}
                            />
                        )}
                    </div>
                ) : (
                    <div className={styles.BazaarGrid__hitsCount}>
                        <h4 className={styles.BazaarGrid__hitsCount__products}>
                            {isLoading(loadingState) ? (
                                <LoadingPlaceholder width={78} />
                            ) : (
                                interpolate(BZ2_counter_PRODUCTS, {
                                    counter: formatThousands(hits),
                                    count: hits,
                                })
                            )}
                        </h4>
                        <div className={styles.BazaarGrid__hitsCount__line} />
                        {hits !== null && (
                            <QualityCheckboxes
                                qualities={quality}
                                handmadeCount={handmadeCount}
                                jobsCount={jobsCount}
                                newCount={newCount}
                                newCountHidden={selectedCat === "prams"}
                                oldCount={oldCount}
                                onChange={onQualityChange}
                                servicesCount={servicesCount}
                            />
                        )}
                    </div>
                )}

                <div className={styles.BazaarGrid__content}>
                    {hits !== null && (
                        <div className={styles.BazaarGrid__secondaryFilters}>
                            <div className={styles.BazaarGrid__secondaryFilters__left} />
                            <div className={styles.BazaarGrid__secondaryFilters__right}>
                                {!isMobile && this.renderAdditionalFilters(gridSort)}
                                {isAuthenticated(requestUser) && (
                                    <SimpleSelect
                                        className={cx(
                                            styles.BazaarGrid__secondaryFilters__right__item,
                                            styles.BazaarGrid__sellers,
                                        )}
                                        selected={filter.sellers || "all"}
                                        options={sellersValues}
                                        type="bigger"
                                        onChange={onSellersChange}
                                    />
                                )}
                                <SimpleSelect
                                    className={cx(
                                        styles.BazaarGrid__secondaryFilters__right__item,
                                        styles.BazaarGrid__gridSort,
                                    )}
                                    selected={gridSort}
                                    options={sortingValues}
                                    type="bigger"
                                    onChange={onGridSortChange}
                                />
                            </div>
                        </div>
                    )}

                    {isMobile && this.renderAdditionalFilters(gridSort)}

                    {canEditWiki(requestUserPermissions) && (
                        <div className={styles.BazaarGrid__copyFilter}>
                            <button
                                className={styles.BazaarGrid__copyFilter__button}
                                onClick={this.onCopyFilterClick}
                            >
                                {BAZAAR_GRID_COPY_FILTER}
                            </button>
                        </div>
                    )}

                    {selectedCat === "prams" && (
                        <StrollerbaseBanner className={styles.BazaarGrid__strollerbaseBanner} />
                    )}

                    {watchdog && isSuccess(loadingState) && (
                        <Watchdog
                            className={styles.BazaarGrid__watchdog}
                            onSubscribe={this.onWatchdogSubscribe}
                            onUnsubscribe={this.onWatchdogUnsubscribe}
                            subscribed={watchdog}
                        />
                    )}

                    {isMobile && (
                        <AdSlot
                            zone="Market Section - Top 01 - Mobile"
                            location={location}
                            marginBottomWithAd={10}
                        />
                    )}

                    <div ref={this.productsRef} className={styles.BazaarGrid__products}>
                        <LoadingSwitch
                            loadingState={loadingState}
                            onLoad={onLoad}
                            onRenderSuccess={this.renderSuccess}
                            onRenderInit={this.renderInit}
                            hasData={products && products.length > 0}
                        />
                    </div>

                    {maxPage > 1 && (
                        <Panel>
                            <Pager
                                className={styles.BazaarGrid__pager}
                                history={history}
                                numberOfPages={maxPage}
                                isMobile={isMobile}
                                position="bottom"
                                page={filter.page}
                                to={pagerTo}
                            />
                        </Panel>
                    )}

                    {hasSomeFilter && isSuccess(loadingState) && (
                        <Watchdog
                            className={styles.BazaarGrid__watchdog}
                            onSubscribe={this.onWatchdogSubscribe}
                            onUnsubscribe={this.onWatchdogUnsubscribe}
                            subscribed={watchdog}
                        />
                    )}

                    {hits > 0 && !isMobile && (
                        <AdSlot
                            zone="Market Section - Mid 02 - Desktop"
                            location={location}
                            className={styles.BazaarGrid__billboard}
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderInit = () => {
        const { loadingState, filter, hits, products } = this.props;

        if (loadingState === LoadingState.INIT && products && products.length) {
            return this.renderSuccess();
        }

        const maxPage = hits ? Math.ceil(hits / ITEMS_PER_PAGE) : 1;
        // Products count calculated from hits so that we can display correct number of placeholders
        const productsCount =
            filter.page < maxPage ? ITEMS_PER_PAGE : hits % ITEMS_PER_PAGE || ITEMS_PER_PAGE;

        return <GridPlaceholder productsCount={productsCount} />;
    };

    private renderSuccess = () => {
        const {
            requestUser,
            requestUserPermissions,
            isMobile,
            loadingState,
            location,
            products,

            strollers,
            strollerPhotosMap,

            filter,
            checked,
            complaintsPerProduct,
        } = this.props;

        const isModerator = canReportProducts(requestUserPermissions);
        const isAdmin = canManageBazaar(requestUserPermissions);

        return (
            <Grid
                requestUser={requestUser}
                products={products}
                strollers={strollers}
                strollerPhotosMap={strollerPhotosMap}
                isModerator={isModerator}
                isAdmin={isAdmin}
                checked={checked}
                showCreatedTime={
                    filter.settings.gridSort === "creation" ||
                    filter.settings.gridSort === "watchdog"
                }
                complaintsPerProduct={complaintsPerProduct}
                isMobile={isMobile}
                location={location}
                isLoading={isLoading(loadingState)}
                onItemClick={this.trackItemClick}
                onStrollerItemClick={this.trackStrollerItemClick}
            />
        );
    };

    private renderAdditionalFilters = (gridSort) => {
        const {
            filter,
            onCleanSortChange,
            onComplaintsChange,
            onNotMarkedChange,
            requestUserPermissions,
        } = this.props;
        const isModerator = canReportProducts(requestUserPermissions);
        const selectedCat = filter.cats.length ? filter.cats[0] : null;
        return (
            (isModerator ||
                (canUseCleanSort(requestUserPermissions) && gridSort !== "watchdog")) && (
                <div className={styles.BazaarGrid__additionalFilters}>
                    {canUseCleanSort(requestUserPermissions) &&
                        gridSort !== "watchdog" &&
                        selectedCat !== "prams" && (
                            <label className={styles.BazaarGrid__additionalFilters__filter}>
                                <input
                                    type="checkbox"
                                    checked={!filter.settings.cleanSort}
                                    onChange={onCleanSortChange}
                                />
                                &nbsp;
                                {BZ2_COMMERCIAL_FIRST}
                            </label>
                        )}
                    {isModerator && (
                        <>
                            <label
                                className={cx(
                                    styles.BazaarGrid__additionalFilters__filter,
                                    styles["BazaarGrid__additionalFilters__filter--moderator"],
                                )}
                            >
                                <input
                                    type="checkbox"
                                    checked={Boolean(filter.notMarked)}
                                    onChange={onNotMarkedChange}
                                />
                                &nbsp;
                                {BZ2_ONLY_NOT_MARKED}
                            </label>
                            <label
                                className={cx(
                                    styles.BazaarGrid__additionalFilters__filter,
                                    styles["BazaarGrid__additionalFilters__filter--moderator"],
                                )}
                            >
                                <input
                                    type="checkbox"
                                    checked={Boolean(filter.complaints)}
                                    onChange={onComplaintsChange}
                                />
                                &nbsp;
                                {BZ2_ONLY_REPORTED}
                            </label>
                        </>
                    )}
                </div>
            )
        );
    };

    private onWatchdogSubscribe = (event: React.MouseEvent<HTMLAnchorElement>) => {
        const { onWatchdogSubscribe, match, filter, routeParams, location } = this.props;
        onWatchdogSubscribe(filter, watchdogKey(filter, match.path, routeParams), location);
    };

    private onWatchdogUnsubscribe = (event: React.MouseEvent<HTMLAnchorElement>) => {
        const { onWatchdogUnsubscribe, match, filter, routeParams, location } = this.props;
        onWatchdogUnsubscribe(filter, watchdogKey(filter, match.path, routeParams), location);
    };

    private trackView = () => {
        const { loadingState, products, filter, requestUser, onStoreEvent } = this.props;

        // Track only if we have successfully loaded data
        if (!isSuccess(loadingState)) {
            return;
        }

        const currentFilterHash = hashFilter(filter);
        if (this.lastFilterHash !== currentFilterHash) {
            this.viewUuid = uuid();
            this.lastFilterHash = currentFilterHash;

            const start = filter.page ? (filter.page - 1) * ITEMS_PER_PAGE : filter.start;
            const filterCategory = filter.cats.length > 0 ? filter.cats[0] : null;
            // generates sentry errors
            // onStoreEvent('bazaar_grid_view', {
            //     view_id: this.viewUuid,
            //     products: JSON.stringify(products.map((p) => p.id)),
            //     filter: JSON.stringify(filter),
            //     filter_category: filterCategory,
            //     filter_locations: JSON.stringify(filter.locations),
            //     filter_places: JSON.stringify(filter.places),
            //     filter_price_max: filter.price_hi,
            //     filter_price_min: filter.price_lo,
            //     filter_query: filter.q,
            //     filter_start: start,
            //     filter_users: JSON.stringify(filter.users),
            //     filter_sellers: filter.sellers,
            //     filter_complaints: filter.complaints,
            //     filter_not_marked: filter.notMarked,
            //     filter_clean_sort: filter.settings.cleanSort,
            //     filter_grid_sort: filter.settings.gridSort,
            //     filter_quality: JSON.stringify(filter.settings.quality),
            //     ...(filterCategory ? {
            //         // Facebook Conversions API event's content data
            //         fbc_props_json: isAuthenticated(requestUser) ? JSON.stringify({
            //             content_type: 'product',
            //             content_category: 'MK',
            //             // +OTAZKA moze tam byt anglicky slug?
            //             content_name: filterCategory,
            //         }) : '',
            //     } : {}),
            // });
        }
    };

    private trackItemClick = (product: ProductEntity) => {
        // const { onStoreEvent } = this.props;
        //
        // if (this.viewUuid) {
        //     onStoreEvent('bazaar_grid_item_click', {
        //         view_id: this.viewUuid,
        //         product: product.id,
        //     });
        // }
    };

    private trackStrollerItemClick = (
        stroller: StrollerEntity,
        variantId: number,
        gridIndex: number,
    ) => {
        const { filter, onStoreEvent } = this.props;
        if (this.viewUuid) {
            onStoreEvent("bazaar_stroller_grid_item_click", {
                view_id: this.viewUuid,
                stroller_slug: stroller.slug,
                stroller_variant_id: String(variantId || ""),
                item_position: String(gridIndex),
                filter_type: String(filter?.props?.p_type),
                filter_brand: String(filter?.props?.p_brand),
                filter_model: String(filter?.props?.p_brand_model),
            });
        }
    };

    private onCopyFilterClick = () => {
        const { filter, onShowSuccessToast } = this.props;
        const optimizedFilter = Object.keys(filter).reduce<Partial<Filter>>((acc, cur) => {
            const val = filter[cur];
            if (cur === "props") {
                // Remove empty props
                const newProps = Object.keys(val).reduce((acc2, cur2) => {
                    if (val[cur2]?.length) {
                        acc2[cur2] = val[cur2];
                    }
                    return acc2;
                }, {});
                if (Object.keys(newProps).length) {
                    acc.props = newProps;
                }
            } else if (cur === "page" || cur === "settings") {
                // Ignore page and settings
                return acc;
            } else if (val && (!Array.isArray(val) || val.length > 0)) {
                acc[cur] = val;
            }
            return acc;
        }, {});
        const result = JSON.stringify(optimizedFilter);
        // https://developer.mozilla.org/en-US/docs/Web/API/btoa#unicode_strings
        // Solution with ASCII base64 interoperability
        const data = btoa(
            encodeURIComponent(result).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
                return String.fromCharCode(parseInt(p1, 16));
            }),
        );
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        navigator.clipboard
            .writeText(data)
            .then(() => {
                onShowSuccessToast(BAZAAR_GRID_COPY_FILTER_SUCCESS);
            })
            .catch((err) => {
                logger.error(err);
                window.prompt(BAZAAR_GRID_COPY_FILTER_PROMPT, data);
            });
    };
}

const BazaarGridWithLogic: React.FunctionComponent<PageProps> = (props) => {
    const { onLoad, match, routeParams, location } = props;
    const gridProps = useGrid(props);

    const onLoadCb = useCallback(() => {
        onLoad(
            gridProps.filter,
            gridProps.filter ? watchdogKey(gridProps.filter, match.path, routeParams) : null,
            location,
        );
    }, [onLoad, match, routeParams, gridProps.filter, location]);

    return (
        <GridPageLogic
            className={styles.BazaarGrid}
            filter={gridProps.filter}
            facets={gridProps.facets}
            loadingState={gridProps.loadingState}
            errorMessage={gridProps.errorMessage}
            isMobile={props.isMobile}
            location={props.location}
            scroll={props.scroll}
            onLoad={onLoadCb}
            openedFacet={gridProps.openedFacet}
            generateCategoryTo={gridProps.generateCategoryTo}
            onFacetChange={gridProps.onFacetChange}
            onPriceChange={gridProps.onPriceChange}
            onPlacesRadiusChange={gridProps.onPlacesRadiusChange}
            onNavigationActivate={props.onNavigationActivate}
            onNavigationStoreBack={props.onNavigationStoreBack}
        >
            <BazaarGrid
                {...props}
                {...gridProps}
                loadingState={props.loadingState}
                onLoad={onLoadCb}
            />
        </GridPageLogic>
    );
};

BazaarGridWithLogic.displayName = "BazaarGridWithLogic";

export default BazaarGridWithLogic;
// export default BazaarGrid;
